import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getProduzioniPerTipologia,
  type Granularita, type ProduzioniPerTipologiaResponse,
} from '../../api/dashboard';
import Modal from '../ui/Modal';
import { fmtKg } from '../../lib/saldo';

interface Props {
  /** Label del bucket cliccato, nella forma che il backend ha prodotto. */
  periodo: string;
  granularita: Granularita;
  /** Chiave del segmento = `produzioni.materiale` (il CER snapshot). */
  materiale: string;
  /** Etichetta leggibile del segmento, per il titolo. */
  etichetta: string;
  /** kg del segmento cliccato: serve a mostrare che il totale coincide. */
  kgSegmento: number;
  onClose: () => void;
}

/**
 * Dettaglio di UNA fetta della barra «Produzioni»: i kg per tipologia.
 *
 * Il grafico somma i pesi delle PRODUZIONI, la tipologia sta sui BIG BAG: il
 * backend somma i sacchi di quelle produzioni, e i totali coincidono perché
 * «peso produzione == somma dei suoi sacchi» è invariante. Qui NON si ricalcola
 * nulla: si mostra `kg_totali` della risposta, che è l'unico numero che può
 * dare la stessa cifra della barra.
 */
export default function DrillDownTipologieModal({
  periodo, granularita, materiale, etichetta, kgSegmento, onClose,
}: Props) {
  const { t } = useTranslation();
  const [dati, setDati] = useState<ProduzioniPerTipologiaResponse | null>(null);
  const [errore, setErrore] = useState(false);

  useEffect(() => {
    let vivo = true;
    void (async () => {
      try {
        const r = await getProduzioniPerTipologia(periodo, materiale, granularita);
        if (vivo) setDati(r);
      } catch (e) {
        console.error('dashboard/produzioni-per-tipologia', e);
        if (vivo) setErrore(true);
      }
    })();
    return () => { vivo = false; };
  }, [periodo, materiale, granularita]);

  return (
    <Modal
      title={t('dashboard.drilldown.titolo', { materiale: etichetta, periodo })}
      subtitle={t('dashboard.drilldown.sottotitolo', { kg: fmtKg(kgSegmento) })}
      testid="drilldown-tipologie"
      onClose={onClose}
    >
      {errore ? (
        <p className="text-sm text-accent-danger" data-testid="drilldown-errore">
          {t('dashboard.drilldown.errore')}
        </p>
      ) : !dati ? (
        <p className="text-sm text-ink-soft">{t('dashboard.drilldown.caricamento')}</p>
      ) : dati.righe.length === 0 ? (
        <p className="text-sm text-ink-soft" data-testid="drilldown-vuoto">
          {t('dashboard.drilldown.vuoto')}
        </p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs font-semibold text-ink-soft uppercase tracking-wide">
              <th className="py-1.5">{t('dashboard.drilldown.col_tipologia')}</th>
              <th className="py-1.5 text-right">{t('dashboard.drilldown.col_kg')}</th>
              <th className="py-1.5 text-right">{t('dashboard.drilldown.col_big_bags')}</th>
            </tr>
          </thead>
          <tbody>
            {/* Chiave sul NOME quando manca l'id: le voci senza tipologia sono
                DUE e diverse — i sacchi senza definizione («Non specificata») e
                il peso che nessun sacco copre («Senza big bag») — e con una
                chiave sola si sovrapporrebbero. */}
            {dati.righe.map((r) => (
              <tr key={r.tipologia_id ?? r.tipologia_nome} className="border-t border-stone-mid/60"
                data-testid={`drilldown-riga-${r.tipologia_id ?? 'nessuna'}`}>
                <td className="py-1.5">{r.tipologia_nome}</td>
                <td className="py-1.5 text-right font-medium">{fmtKg(r.kg)}</td>
                <td className="py-1.5 text-right text-ink-mid">{r.big_bags}</td>
              </tr>
            ))}
            <tr className="border-t-2 border-stone-mid font-semibold">
              <td className="py-1.5">{t('dashboard.drilldown.totale')}</td>
              <td className="py-1.5 text-right" data-testid="drilldown-totale">{fmtKg(dati.kg_totali)}</td>
              <td />
            </tr>
          </tbody>
        </table>
      )}
    </Modal>
  );
}
